import { compareGameNames, NamedGame } from "./grouping";

/*
  The featured section's ranking (§4.2, §4.3, D15).

  The order of operations is the whole point: rank by cardsGraded, THEN slice, THEN
  optionally re-order that slice alphabetically. Sorting alphabetically before the slice
  gives the first N games by name — which is exactly the admin behaviour D15 rejects.
*/

/*
  Ten (Robert, 2026-10-19: "add the top ten so add two more games"), superseding D33's eight.
  Still one number for the whole site: the home page's featured games and a game's top
  graded sets are the same idea at two scales, and they look like siblings only when they
  count the same. Both grids are five wide at the widest breakpoint now (lg:grid-cols-5),
  so ten is two full rows there — eight had left a row of three stranded on the home page.
*/
export const FEATURED_LIMIT = 10;

export interface RankableGame extends NamedGame {
  cardsGraded: number | null;
}

/*
  cardsGraded is null when CatalogService's count cache throws; it sorts as 0 and
  displays as 0 (§4.2). Ties break by name so the Top 10 is deterministic and testable.
*/
export const gradedCountValue = (cardsGraded: number | null | undefined): number =>
  typeof cardsGraded === "number" && isFinite(cardsGraded) ? cardsGraded : 0;

export const compareByCardsGraded = <T extends RankableGame>(a: T, b: T): number => {
  const difference = gradedCountValue(b.cardsGraded) - gradedCountValue(a.cardsGraded);
  return difference !== 0 ? difference : compareGameNames(a, b);
};

export const rankByCardsGraded = <T extends RankableGame>(games: readonly T[]): T[] =>
  games.slice().sort(compareByCardsGraded);

export interface FeaturedOptions {
  /* The Sort control's "A-Z" setting. Re-orders the ten; never selects them. */
  alphabetical?: boolean;
  limit?: number;
}

export const selectFeaturedGames = <T extends RankableGame>(
  games: readonly T[],
  options: FeaturedOptions = {}
): T[] => {
  const { alphabetical = false, limit = FEATURED_LIMIT } = options;
  const topGames = rankByCardsGraded(games).slice(0, limit);
  return alphabetical ? topGames.sort(compareGameNames) : topGames;
};
